import { useUpdateExerciseToPatient } from '_api';
import { AddExerciseToPatientPayload } from '_api/types';
import { SEVERITY } from '_constants/derived';
import { AlertMessage, BaseModal } from 'components/molecules';
import { AddExerciseToPatientForm } from 'components/organisms/forms';
import {
  hasNonblankPatientInstructions,
  normalizePatientInstructions,
} from 'components/organisms/forms/patientInstructions';
import React, { useEffect, useState } from 'react';

import { EditPatientProfileProps } from './types';

export const EditExerciseToPatientProfileModal = ({
  open,
  onClose,
  initValue,
  label,
}: EditPatientProfileProps<AddExerciseToPatientPayload>) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { mutateAsync: editExerciseToPatient } = useUpdateExerciseToPatient();
  const [payload, setPayload] = useState<AddExerciseToPatientPayload>(initValue);

  useEffect(() => {
    if (open) {
      setErrorMessage('');
      setShowSuccess(false);
      setPayload(initValue);
    }
  }, [initValue, open]);

  const onUpdate = async (key: any, arg: any) => {
    setPayload((current) => ({
      ...current,
      [key]: arg,
    }));
  };

  const onSubmit = () => {
    if (!hasNonblankPatientInstructions(payload.instructionList)) {
      setErrorMessage('At least one instruction is required.');
      return;
    }
    editExerciseToPatient({
      ...payload,
      instructionList: normalizePatientInstructions(payload.instructionList),
    })
      .then(() => {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 1500);
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          setErrorMessage(
            'Unexpected error occured. Please close the modal and try again.',
          );
        } else if (error.message) {
          setErrorMessage(`ERROR: ${error.message}`);
        } else {
          setErrorMessage('Unexpected error. Please Try again.');
        }
      });
  };

  const initValues = {
    ...payload,
    defaultTargetSide: payload?.defaultTargetSide,
  };

  return (
    <BaseModal open={open} onClose={onClose} title={`Edit Exercise - ${label}`}>
      <AddExerciseToPatientForm
        initValue={initValues}
        onBack={onClose}
        onSubmit={onSubmit}
        onUpdate={onUpdate}
        hidePatientSelect={true}
        isEdit={true}
      />
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={'Successfully updated exercise.'}
      />
      <AlertMessage
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        severity={SEVERITY.error}
        message={errorMessage}
      />
    </BaseModal>
  );
};
